import { create } from "zustand";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { withTimeout } from "@/lib/timeout";
import { sendResetEmail } from "@/services/password";

/**
 * Auth + current-user store.
 *
 * On mount, `init()` subscribes to `onAuthStateChange` and hydrates a
 * `CurrentUser` row from `app_users` for the signed-in `auth.users` id.
 *
 * MFA gating is derived from the Supabase Auth assurance level
 * (currentLevel / nextLevel) — NOT from a module-local flag — so it
 * survives page reloads and tab switches.
 */

export type UserRole = "admin_user" | "route_manager" | "app_user";
export type AuthStatus = "loading" | "authed" | "pending_mfa" | "unauthed";
export type SignInDuration = "keep" | "8h";

export interface CurrentUser {
  id: string;
  email: string;
  fullName: string;
  role: UserRole;
  teamId: string;
  hasMfa: boolean;
}

interface AuthState {
  status: AuthStatus;
  session: Session | null;
  user: CurrentUser | null;
  mfaFactorId: string | null;
  /** Bootstrap: read existing session + subscribe to auth changes. Idempotent. */
  init: () => Promise<void>;
  signInWithPassword: (
    email: string,
    password: string,
    opts?: { duration?: SignInDuration },
  ) => Promise<{ error: string | null; mfaRequired: boolean }>;
  verifyMfaCode: (code: string) => Promise<{ error: string | null }>;
  sendPasswordReset: (email: string) => Promise<{ error: string | null }>;
  /** Re-fetch the app_users row + MFA factor list for the current session. */
  rehydrateUser: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AUTH_EXPIRY_KEY = "myroute:auth:expiresAt";
let initialized = false;
let unsubscribe: (() => void) | null = null;
// Dedupe overlapping rehydrates triggered by back-to-back onAuthStateChange
// events (SIGNED_IN + MFA_CHALLENGE_VERIFIED + TOKEN_REFRESHED can fire within
// a few ms during MFA verify). Two concurrent listFactors() calls during the
// AAL1->AAL2 swap can deadlock supabase-js's internal lock and hang the UI.
let rehydrateInFlight: Promise<void> | null = null;

function readAuthExpiry(): number | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(AUTH_EXPIRY_KEY);
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function writeAuthExpiry(duration: SignInDuration) {
  if (typeof window === "undefined") return;
  if (duration === "keep") {
    window.localStorage.removeItem(AUTH_EXPIRY_KEY);
    return;
  }
  window.localStorage.setItem(AUTH_EXPIRY_KEY, String(Date.now() + 8 * 60 * 60 * 1000));
}

function clearAuthExpiry() {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(AUTH_EXPIRY_KEY);
}

async function hydrateUser(session: Session | null): Promise<CurrentUser | null> {
  if (!session) return null;
  const { data, error } = await supabase
    .from("app_users")
    .select("id, email, full_name, role, team_id")
    .eq("id", session.user.id)
    .maybeSingle();

  if (error) {
    console.error("[auth] failed to hydrate app_users row:", error.message);
    return null;
  }
  if (!data) return null;
  // Compute hasMfa once at hydrate time. Cheap (1 listFactors call) and
  // avoids forcing every consumer of the store to make their own request.
  let hasMfa = false;
  try {
    const factors = await supabase.auth.mfa.listFactors();
    hasMfa = (factors.data?.totp ?? []).some((f) => f.status === "verified");
  } catch {
    hasMfa = false;
  }
  return {
    id: data.id,
    email: data.email,
    fullName: data.full_name,
    role: data.role as UserRole,
    teamId: data.team_id,
    hasMfa,
  };
}

/**
 * Returns the TOTP factor id the user must satisfy *right now*, or null
 * if MFA is either not enrolled or already satisfied at this AAL.
 *
 * - `currentLevel === nextLevel` → user has met the highest required level.
 * - `currentLevel === "aal1" && nextLevel === "aal2"` → must challenge.
 */
async function getPendingMfaFactorId(session: Session | null): Promise<string | null> {
  if (!session) return null;
  const aal = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (aal.error) return null;
  if (aal.data?.currentLevel === aal.data?.nextLevel) return null;
  const factors = await supabase.auth.mfa.listFactors();
  if (factors.error) return null;
  const active = factors.data?.totp?.find((f) => f.status === "verified");
  return active?.id ?? null;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: "loading",
  session: null,
  user: null,
  mfaFactorId: null,

  init: async () => {
    if (initialized) return;
    initialized = true;

    const { data } = await supabase.auth.getSession();
    const initialSession = data.session ?? null;
    const expiresAt = readAuthExpiry();
    if (initialSession && expiresAt != null && Date.now() > expiresAt) {
      await supabase.auth.signOut();
      clearAuthExpiry();
      set({ session: null, user: null, status: "unauthed", mfaFactorId: null });
      return;
    }

    const [initialUser, pendingFactorId] = await Promise.all([
      hydrateUser(initialSession),
      getPendingMfaFactorId(initialSession),
    ]);
    set({
      session: initialSession,
      user: initialUser,
      status: initialSession && initialUser ? (pendingFactorId ? "pending_mfa" : "authed") : "unauthed",
      mfaFactorId: pendingFactorId,
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      // CRITICAL: do NOT call any `supabase.auth.*` method synchronously
      // inside this callback. The callback fires while supabase-js holds
      // its internal auth lock; any nested auth/postgrest call will block
      // waiting for that same lock and deadlock the verify/refresh flow.
      // The documented fix is to defer with setTimeout(..., 0) so the
      // async work runs after the lock is released.
      // Ref: https://github.com/supabase/auth-js/issues/762
      setTimeout(async () => {
        const expiresAt = readAuthExpiry();
        if (session && expiresAt != null && Date.now() > expiresAt) {
          await supabase.auth.signOut();
          clearAuthExpiry();
          set({ session: null, user: null, status: "unauthed", mfaFactorId: null });
          return;
        }

        if (!session) {
          set({ session: null, user: null, status: "unauthed", mfaFactorId: null });
          return;
        }

        // Dedupe overlapping rehydrates (SIGNED_IN + MFA_CHALLENGE_VERIFIED
        // + TOKEN_REFRESHED can fire within milliseconds of each other).
        if (rehydrateInFlight) {
          await rehydrateInFlight;
          return;
        }
        rehydrateInFlight = (async () => {
          const pendingFactorId = await getPendingMfaFactorId(session);
          const user = await hydrateUser(session);
          set({
            session,
            user,
            status: session && user ? (pendingFactorId ? "pending_mfa" : "authed") : "unauthed",
            mfaFactorId: pendingFactorId,
          });
        })();
        try {
          await rehydrateInFlight;
        } finally {
          rehydrateInFlight = null;
        }
      }, 0);
    });
    unsubscribe = () => sub.subscription.unsubscribe();
  },

  signInWithPassword: async (email, password, opts) => {
    const duration = opts?.duration ?? "keep";
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message, mfaRequired: false };

    writeAuthExpiry(duration);
    const session = data.session ?? null;
    const [user, pendingFactorId] = await Promise.all([
      hydrateUser(session),
      getPendingMfaFactorId(session),
    ]);
    set({
      session,
      user,
      status: pendingFactorId ? "pending_mfa" : session && user ? "authed" : "unauthed",
      mfaFactorId: pendingFactorId,
    });

    return { error: null, mfaRequired: !!pendingFactorId };
  },

  verifyMfaCode: async (code) => {
    try {
      // Refetch the factor id if the store doesn't have it (covers
      // multi-tab + post-refresh cases where the store was rehydrated from
      // an AAL1 session but no signIn call was made in this tab).
      const factorId = get().mfaFactorId ?? (await withTimeout(getPendingMfaFactorId(get().session), 8_000, "MFA lookup"));
      if (!factorId) return { error: "Authenticator is not enabled for this account." };

      // Single round-trip instead of challenge() + verify(). Halves latency
      // and avoids a state window where the challenge id is valid but the
      // token hasn't upgraded yet.
      const { error: verifyError } = await withTimeout(
        supabase.auth.mfa.challengeAndVerify({ factorId, code }),
        10_000,
        "MFA verify",
      );
      if (verifyError) return { error: verifyError.message };

      // Optimistically mark authed. onAuthStateChange will fire with the
      // new AAL2 session and rehydrate `user` + `hasMfa` via the deduped
      // path in init(). Avoid calling hydrateUser here — a second concurrent
      // listFactors() during the AAL swap is what was hanging the flow.
      set({ status: "authed", mfaFactorId: null });
      return { error: null };
    } catch (e) {
      return { error: e instanceof Error ? e.message : "Verification failed." };
    }
  },

  sendPasswordReset: async (email) => {
    const trimmed = email.trim();
    if (!trimmed) return { error: "Enter your email first." };
    try {
      // Routes through our send-password-reset edge function (Resend) so
      // delivery doesn't depend on a SMTP provider being configured in the
      // Supabase dashboard. The function accepts an unauthed { email } body
      // and never reveals whether the address exists.
      await sendResetEmail({ email: trimmed });
      return { error: null };
    } catch (e) {
      return { error: e instanceof Error ? e.message : "Failed to send reset link." };
    }
  },

  signOut: async () => {
    clearAuthExpiry();
    await supabase.auth.signOut();
    set({ session: null, user: null, status: "unauthed", mfaFactorId: null });
  },

  rehydrateUser: async () => {
    const session = get().session;
    if (!session) return;
    const user = await hydrateUser(session);
    if (user) set({ user });
  },
}));

// Test/HMR helper — not used in production paths.
export function _resetAuthStoreForTests() {
  unsubscribe?.();
  unsubscribe = null;
  initialized = false;
  clearAuthExpiry();
  useAuthStore.setState({ status: "loading", session: null, user: null, mfaFactorId: null });
}
